import type { InstagramPartnerPermissionStatusWire } from "@common/interfaces/adsManagerGuidedEdit";
import {
  resolvePartnershipPartnerRequestIdentity,
  type LookupPartnershipPartnerPermissionResult,
} from "@common/utils/partnershipPartnerOptions";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import { isSameMetaAssetId } from "./partnerIdentitySelectOptions";
import {
  resolveCreativePartnerFacebookPage,
  resolveCreativePartnerInstagramAccount,
  resolvePartnerFacebookPageLinkedToInstagram,
} from "./resolveCreativePartnerDisplay";

function partnerFacebookPageIdExcludingAdvertiser(
  pageId: string | undefined,
  advertiserPageId: string | undefined
): string | undefined {
  const trimmed = pageId?.trim();
  if (!trimmed || isSameMetaAssetId(trimmed, advertiserPageId)) {
    return undefined;
  }
  return trimmed;
}

export type PartnershipPartnerPermissionLookupParams = {
  ad_account_id: string;
  advertiser_page_id?: string;
  advertiser_instagram_user_id?: string;
  partner_facebook_page_id?: string;
  partner_instagram_user_id?: string;
  partner_instagram_username?: string;
};

export function partnershipPermissionFieldsFromLookup(
  result: LookupPartnershipPartnerPermissionResult
): {
  partnership_permission_status: InstagramPartnerPermissionStatusWire;
  partnership_status_label: string;
  partnership_has_permission_row: boolean;
  partnership_permission_url?: string;
  partnership_permission_id?: string;
  partnership_select_badge: FacebookPage["partnership_select_badge"];
  partnership_select_badge_label: string;
} {
  return {
    partnership_permission_status: result.partnership_permission_status,
    partnership_status_label: result.partnership_status_label,
    partnership_has_permission_row: result.partnership_has_permission_row,
    ...(result.partnership_permission_url
      ? { partnership_permission_url: result.partnership_permission_url }
      : {}),
    ...(result.partnership_permission_id
      ? { partnership_permission_id: result.partnership_permission_id }
      : {}),
    partnership_select_badge: result.partnership_select_badge,
    partnership_select_badge_label: result.partnership_select_badge_label,
  };
}

export function mergePartnershipPermissionLookupIntoFacebookPage(
  page: FacebookPage,
  result: LookupPartnershipPartnerPermissionResult
): FacebookPage {
  return {
    ...page,
    ...partnershipPermissionFieldsFromLookup(result),
  };
}

export function mergePartnershipPermissionLookupIntoInstagramAccount(
  account: InstagramAccount,
  result: LookupPartnershipPartnerPermissionResult
): InstagramAccount {
  return {
    ...account,
    ...partnershipPermissionFieldsFromLookup(result),
  };
}

/** Wire partner ids for lazy lookup — matches API params and avoids a second fetch after enrichment. */
export function resolvePartnershipPermissionLookupWire(args: {
  creative: CreativeBlock;
  allFacebookPages: readonly FacebookPage[];
  allInstagramAccounts: readonly InstagramAccount[];
  adAccountId?: string;
  advertiserInstagramUserId?: string;
}): {
  lookupKey: string | null;
  page: FacebookPage | undefined;
  instagram: InstagramAccount | undefined;
  identity: ReturnType<typeof resolvePartnershipPartnerRequestIdentity>;
} {
  const advertiserPageId = args.creative.fbPage?.id?.trim();
  const page = resolveCreativePartnerFacebookPage(
    args.creative,
    args.allFacebookPages
  );
  const instagram = resolveCreativePartnerInstagramAccount(
    args.creative,
    args.allInstagramAccounts
  );
  const inferredPage = instagram
    ? resolvePartnerFacebookPageLinkedToInstagram(
        instagram,
        args.allFacebookPages
      )
    : undefined;
  const effectivePage =
    page && !isSameMetaAssetId(page.id, advertiserPageId)
      ? page
      : inferredPage &&
          !isSameMetaAssetId(inferredPage.id, advertiserPageId)
        ? inferredPage
        : undefined;

  const identity = resolvePartnershipPartnerRequestIdentity({
    partnerInstagramUserId:
      instagram?.id ?? args.creative.partnerInstagramUserId,
    partnerInstagramUsername:
      instagram?.username ?? args.creative.partnerInstagramUsername,
    partnerFacebookPageId: partnerFacebookPageIdExcludingAdvertiser(
      effectivePage?.id ?? args.creative.partnerFacebookPageId,
      advertiserPageId
    ),
    linkedInstagramUserId: effectivePage?.linked_instagram_user_id,
    linkedInstagramUsername: effectivePage?.linked_instagram_username,
  });

  return {
    lookupKey: buildPartnershipPermissionLookupIdentityKey({
      ad_account_id: args.adAccountId?.trim() ?? "",
      advertiser_page_id: args.creative.fbPage?.id?.trim(),
      advertiser_instagram_user_id: args.advertiserInstagramUserId?.trim(),
      partner_facebook_page_id: identity.partnerFacebookPageId,
      partner_instagram_user_id: identity.partnerInstagramUserId,
      partner_instagram_username: identity.partnerInstagramUsername,
    }),
    page: effectivePage,
    instagram,
    identity,
  };
}

/** Stable key for deduping lazy permission lookups (wire ids only, not embedded objects). */
export function buildPartnershipPermissionLookupIdentityKey(
  params: PartnershipPartnerPermissionLookupParams
): string | null {
  const adAccountId = params.ad_account_id?.trim();
  const brandIg = params.advertiser_instagram_user_id?.trim();
  if (!adAccountId || !brandIg) {
    return null;
  }

  const partnerFacebookPageId = params.partner_facebook_page_id?.trim() ?? "";
  const partnerInstagramUserId = params.partner_instagram_user_id?.trim() ?? "";
  const partnerInstagramUsername =
    params.partner_instagram_username?.trim().replace(/^@+/, "") ?? "";

  if (
    !partnerFacebookPageId &&
    !partnerInstagramUserId &&
    !partnerInstagramUsername
  ) {
    return null;
  }

  return [
    adAccountId,
    brandIg,
    params.advertiser_page_id?.trim() ?? "",
    partnerFacebookPageId,
    partnerInstagramUserId,
    partnerInstagramUsername,
  ].join("|");
}
